import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import BDrawer from '@ui/components/common/BDrawer';
import AppAlert from '@ui/components/common/Alert';
import TabWithBadge from '@ui/components/TabWithBadge';
import UnassignedAttendeesTab from './UnassignedAttendeesTab';
import ReassignedAttendeesTab from './ReassignedAttendeesTab';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { ClosePickUpLocationAsync, getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestClosePickupLocationAsync, FamilyFestGetPickupLocationsNotPaginatedAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { getAllAttendeePickupAssignmentsAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getAllFamilyFestPickupAssignmentsAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
import { toast } from 'react-toastify';
const displayValue = (value) => {
    if (!value || value.trim() === '')
        return '-';
    return value;
};
const ClosePickupLocationDialog = ({ open, onClose, pickupLocationName, locationId, onCloseSuccess }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [activeTab, setActiveTab] = useState('unassigned');
    const [selectedRows, setSelectedRows] = useState([]);
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const [searchValue, setSearchValue] = useState('');
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    const { familyFestPickupLocationsList, status: familyFestStatus } = useAppSelector((state) => state.familyFestPickupLocation);
    const { pickupLocationResponse } = useAppSelector((state) => state.pickupLocation);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const isFamilyEvent = VDKEventsForEventDetails?.type === EventTypeEnum.Family;
    const [unassignedAttendees, setUnassignedAttendees] = useState([]);
    const [reassignedAttendees, setReassignedAttendees] = useState([]);
    const [pickupLocationSearchValue, setPickupLocationSearchValue] = useState('');
    // Map PickupLocationDto to ReassignPickupLocationItem
    const pickupLocationOptions = useMemo(() => {
        const list = isFamilyEvent ? familyFestPickupLocationsList : pickupLocationsList;
        if (!list)
            return [];
        return list.map((location) => ({
            id: location.id,
            name: location.name,
            address: location.address,
            eventName: location.eventName || '',
            capacity: isFamilyEvent ? undefined : {
                remaining: location.assignedAttendees,
                total: location.totalCapacity
            }
        }));
    }, [pickupLocationsList, familyFestPickupLocationsList, isFamilyEvent]);
    // Fetch pickup locations when dialog opens or search changes
    useEffect(() => {
        if (open && eventId && locationId) {
            if (isFamilyEvent) {
                dispatch(FamilyFestGetPickupLocationsNotPaginatedAsync({
                    includeClosed: false,
                    searchString: pickupLocationSearchValue || undefined,
                    pickupLocationId: locationId
                }));
            }
            else {
                dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                    eventId: eventId,
                    includeClosed: false,
                    searchString: pickupLocationSearchValue || undefined,
                    pickupLocationId: locationId
                }));
            }
        }
    }, [open, eventId, locationId, pickupLocationSearchValue, isFamilyEvent, dispatch]);
    useEffect(() => {
        if (!locationId)
            return;
        if (isFamilyEvent) {
            dispatch(getAllFamilyFestPickupAssignmentsAsync({
                pickupLocationId: locationId,
                searchString: undefined
            })).unwrap()
                .then((data) => {
                if (!data)
                    return;
                setUnassignedAttendees(data.map((assignment) => ({
                    id: assignment.id,
                    name: displayValue(assignment.name),
                    company: displayValue(assignment.companyName ?? assignment.company),
                    location: displayValue(assignment.pickupLocationName),
                    type: displayValue(assignment.attendeeType),
                    primaryPerson: displayValue(assignment.primaryAttendeeName),
                    registered: formatRegisteredDateOnly(assignment.createdDate),
                    hasBirthdayOnEventDate: assignment.hasBirthdayOnEventDate,
                    reassignedTo: undefined,
                    reassignedPickupLocationId: undefined
                })));
            });
        }
        else {
            dispatch(getAllAttendeePickupAssignmentsAsync({
                pickupLocationId: locationId,
                searchString: undefined
            })).unwrap()
                .then((data) => {
                if (!data)
                    return;
                setUnassignedAttendees(data.map((assignment) => ({
                    id: assignment.id,
                    name: displayValue(assignment.name),
                    company: displayValue(assignment.companyName ?? assignment.company),
                    location: displayValue(assignment.pickupLocationName),
                    type: displayValue(assignment.attendeeType),
                    registered: formatRegisteredDateOnly(assignment.createdDate),
                    hasBirthdayOnEventDate: assignment.hasBirthdayOnEventDate,
                    reassignedTo: undefined,
                    reassignedPickupLocationId: undefined
                })));
            });
        }
        setReassignedAttendees([]);
        setSelectedRows([]);
    }, [locationId, pickupLocationResponse?.data]);
    const primaryPersonColumns = isFamilyEvent
        ? [
            {
                field: 'primaryPerson',
                headerName: t('events.eventDetails.transportTab.primaryPerson'),
                flex: 1,
                minWidth: 140
            }
        ]
        : [];
    const unassignedColumns = [
        {
            field: 'name',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'company',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 120
        },
        {
            field: 'location',
            headerName: t('events.eventDetails.transportTab.location'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', title: params.row.location, children: params.row.location }))
        },
        {
            field: 'type',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 100
        },
        ...primaryPersonColumns,
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.2,
            minWidth: 180
        }
    ];
    const reassignedColumns = [
        {
            field: 'name',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'company',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 120
        },
        {
            field: 'type',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 100
        },
        ...primaryPersonColumns,
        {
            field: 'reassignedTo',
            headerName: t('events.eventDetails.transportTab.location'),
            flex: 1.2,
            minWidth: 200,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', title: params.row.reassignedTo, children: params.row.reassignedTo }))
        },
        {
            field: 'registered',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1.2,
            minWidth: 180
        }
    ];
    const handleConfirm = () => {
        if (isFamilyEvent) {
            const familyFestAssignments = reassignedAttendees.map((attendee) => ({
                attendeeAssignmentId: attendee.id,
                pickupLocationId: attendee.reassignedPickupLocationId || ''
            }));
            dispatch(FamilyFestClosePickupLocationAsync({
                pickupLocationId: locationId,
                assignments: familyFestAssignments
            }))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('events.eventDetails.transportTab.closePickupLocationSuccess'));
                    onCloseSuccess?.();
                }
                else {
                    toast.error(response.message || t('events.eventDetails.transportTab.closePickupLocationError'));
                }
                onClose();
            });
        }
        else {
            const assignments = reassignedAttendees.map((attendee) => ({
                attendeeId: attendee.id,
                pickupLocationId: attendee.reassignedPickupLocationId || ''
            }));
            dispatch(ClosePickUpLocationAsync({
                pickupLocationId: locationId,
                assignments: assignments
            }))
                .unwrap()
                .then((response) => {
                if (response.isSuccess) {
                    toast.success(response.message || t('events.eventDetails.transportTab.closePickupLocationSuccess'));
                    onCloseSuccess?.();
                }
                else {
                    toast.error(response.message || t('events.eventDetails.transportTab.closePickupLocationError'));
                }
                onClose();
            });
        }
    };
    const handleReassign = (location, selectedIds) => {
        if (!selectedIds.length) {
            return;
        }
        setReassignedAttendees((prev) => [
            ...prev,
            ...unassignedAttendees
                .filter((a) => selectedIds.includes(a.id))
                .map((a) => ({ ...a, reassignedTo: location.address, reassignedPickupLocationId: String(location.id) }))
        ]);
        setUnassignedAttendees((prev) => prev.filter((a) => !selectedIds.includes(a.id)));
        setSelectedRows([]);
    };
    const dialogContent = (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '20px', children: [_jsx(AppAlert, { severity: 'info', children: _jsx(Typography, { variant: 'body2', component: 'span', children: t('events.eventDetails.transportTab.closePickupLocationWarning') }) }), _jsx(Box, { sx: { width: 'auto' }, children: _jsxs(Tabs, { variant: 'scrollable', value: activeTab, onChange: (_, newValue) => setActiveTab(newValue), sx: {
                        '& .MuiTab-root:not(.Mui-selected)': {
                            color: theme.palette.text.primary
                        },
                        '& .MuiTab-root.Mui-selected': {
                            color: theme.palette.text.primary
                        }
                    }, children: [_jsx(TabWithBadge, { value: 'unassigned', labelText: t('events.eventDetails.transportTab.attendees'), badgeCount: unassignedAttendees.length }), _jsx(TabWithBadge, { value: 'reassigned', labelText: t('events.eventDetails.transportTab.reassignedAttendees'), badgeCount: reassignedAttendees.length })] }) }), activeTab === 'unassigned' ? (_jsx(UnassignedAttendeesTab, { unassignedAttendees: unassignedAttendees, columns: unassignedColumns, pickupLocationOptions: pickupLocationOptions, selectedRows: selectedRows, searchValue: searchValue, onSearchChange: setSearchValue, onSelectionChange: (rows) => setSelectedRows(rows), onPickupLocationSearchChange: setPickupLocationSearchValue, onReassign: handleReassign })) : (_jsx(ReassignedAttendeesTab, { reassignedAttendees: reassignedAttendees, columns: reassignedColumns, searchValue: searchValue, onSearchChange: setSearchValue }))] }));
    const dialogActions = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', onClick: onClose, size: 'large', fullWidth: true, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', onClick: handleConfirm, size: 'large', fullWidth: true, disabled: unassignedAttendees.length > 0, children: t('actions.confirm') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { dialogActions: dialogActions, open: open, onClose: onClose, title: t('events.eventDetails.transportTab.closePickupLocation'), anchor: 'bottom', showBottomBar: true, children: _jsx(Box, { sx: { p: 2 }, children: dialogContent }) }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: onClose, titleWithIcon: t('events.eventDetails.transportTab.closePickupLocation'), titleIcon: '/assets/images/location-pin.png', maxWidth: 'md', backgroundVariant: 'white', closeIcon: true, actions: dialogActions, children: _jsx(Box, { sx: { p: 2 }, children: dialogContent }) }));
};
export default ClosePickupLocationDialog;
